export const CHARACTER_APPEARANCE_SCHEMA_VERSION = 1 as const;
export const PLAYER_APPEARANCE_SAVE_KEY = "vilnius.player-appearance.v1";

export const APPEARANCE_OPTIONS = Object.freeze({
  faceShapes: [
    { id: "round", label: "Round" },
    { id: "oval", label: "Oval" },
    { id: "angular", label: "Angular" },
  ],
  bodyShapes: [
    { id: "slender", label: "Slender" },
    { id: "balanced", label: "Balanced" },
    { id: "broad", label: "Broad" },
  ],
  statures: [
    { id: "short", label: "Short" },
    { id: "average", label: "Average" },
    { id: "tall", label: "Tall" },
  ],
  skinTones: [
    { id: "porcelain", label: "Porcelain" },
    { id: "fair", label: "Fair" },
    { id: "warm", label: "Warm" },
    { id: "olive", label: "Olive" },
    { id: "deep", label: "Deep" },
  ],
  hairStyles: [
    { id: "bald", label: "Bare" },
    { id: "cropped", label: "Cropped" },
    { id: "parted", label: "Side part" },
    { id: "bob", label: "Bob" },
    { id: "bun", label: "Bun" },
  ],
  hairColours: [
    { id: "flax", label: "Flax" },
    { id: "ash", label: "Ash brown" },
    { id: "chestnut", label: "Chestnut" },
    { id: "black", label: "Black" },
    { id: "auburn", label: "Auburn" },
    { id: "silver", label: "Silver" },
  ],
  eyeColours: [
    { id: "grey", label: "Grey" },
    { id: "blue", label: "Blue" },
    { id: "green", label: "Green" },
    { id: "hazel", label: "Hazel" },
    { id: "brown", label: "Brown" },
  ],
  outerwear: [
    { id: "wool_coat", label: "Wool coat" },
    { id: "field_jacket", label: "Field jacket" },
    { id: "cardigan", label: "Cardigan" },
    { id: "raincoat", label: "Raincoat" },
  ],
  lowerGarments: [
    { id: "trousers", label: "Trousers" },
    { id: "wide_trousers", label: "Wide trousers" },
    { id: "skirt", label: "Skirt" },
  ],
  eyewear: [
    { id: "none", label: "None" },
    { id: "round_glasses", label: "Round glasses" },
    { id: "square_glasses", label: "Square glasses" },
  ],
  headwear: [
    { id: "none", label: "None" },
    { id: "knit_cap", label: "Knit cap" },
    { id: "flat_cap", label: "Flat cap" },
    { id: "headscarf", label: "Headscarf" },
  ],
  neckwear: [
    { id: "none", label: "None" },
    { id: "scarf", label: "Scarf" },
    { id: "kerchief", label: "Kerchief" },
  ],
  carriedAccessories: [
    { id: "none", label: "None" },
    { id: "satchel", label: "Satchel" },
    { id: "tote", label: "Tote bag" },
  ],
} as const);

type OptionId<T extends readonly Readonly<{ id: string }>[]> = T[number]["id"];

export type FaceShape = OptionId<typeof APPEARANCE_OPTIONS.faceShapes>;
export type BodyShape = OptionId<typeof APPEARANCE_OPTIONS.bodyShapes>;
export type CharacterStature = OptionId<typeof APPEARANCE_OPTIONS.statures>;
export type SkinTone = OptionId<typeof APPEARANCE_OPTIONS.skinTones>;
export type HairStyle = OptionId<typeof APPEARANCE_OPTIONS.hairStyles>;
export type HairColour = OptionId<typeof APPEARANCE_OPTIONS.hairColours>;
export type EyeColour = OptionId<typeof APPEARANCE_OPTIONS.eyeColours>;
export type Outerwear = OptionId<typeof APPEARANCE_OPTIONS.outerwear>;
export type LowerGarment = OptionId<typeof APPEARANCE_OPTIONS.lowerGarments>;
export type Eyewear = OptionId<typeof APPEARANCE_OPTIONS.eyewear>;
export type Headwear = OptionId<typeof APPEARANCE_OPTIONS.headwear>;
export type Neckwear = OptionId<typeof APPEARANCE_OPTIONS.neckwear>;
export type CarriedAccessory = OptionId<
  typeof APPEARANCE_OPTIONS.carriedAccessories
>;

export interface CharacterAppearance {
  readonly schemaVersion: typeof CHARACTER_APPEARANCE_SCHEMA_VERSION;
  readonly faceShape: FaceShape;
  readonly bodyShape: BodyShape;
  readonly stature: CharacterStature;
  readonly skinTone: SkinTone;
  readonly hairStyle: HairStyle;
  readonly hairColour: HairColour;
  readonly eyeColour: EyeColour;
  readonly outerwear: Outerwear;
  readonly lowerGarment: LowerGarment;
  readonly eyewear: Eyewear;
  readonly headwear: Headwear;
  readonly neckwear: Neckwear;
  readonly carriedAccessory: CarriedAccessory;
  readonly colours: {
    readonly outerwear: number;
    readonly lower: number;
    readonly accent: number;
  };
}

export function sameCharacterAppearance(
  left: Readonly<CharacterAppearance>,
  right: Readonly<CharacterAppearance>,
): boolean {
  return left === right || (
    left.schemaVersion === right.schemaVersion &&
    left.faceShape === right.faceShape &&
    left.bodyShape === right.bodyShape &&
    left.stature === right.stature &&
    left.skinTone === right.skinTone &&
    left.hairStyle === right.hairStyle &&
    left.hairColour === right.hairColour &&
    left.eyeColour === right.eyeColour &&
    left.outerwear === right.outerwear &&
    left.lowerGarment === right.lowerGarment &&
    left.eyewear === right.eyewear &&
    left.headwear === right.headwear &&
    left.neckwear === right.neckwear &&
    left.carriedAccessory === right.carriedAccessory &&
    left.colours.outerwear === right.colours.outerwear &&
    left.colours.lower === right.colours.lower &&
    left.colours.accent === right.colours.accent
  );
}

type AppearanceOverrides = Partial<
  Omit<CharacterAppearance, "schemaVersion" | "colours">
> & {
  readonly colours?: Partial<CharacterAppearance["colours"]>;
};

const OUTERWEAR_PALETTE = [
  0x6f493b,
  0x59665c,
  0x4f5d68,
  0x66535f,
  0x776547,
  0x485b58,
] as const;
const LOWER_PALETTE = [
  0x303839,
  0x40423d,
  0x45404a,
  0x3e4850,
  0x51483f,
] as const;
const ACCENT_PALETTE = [
  0xc29668,
  0x9d6f65,
  0x82979b,
  0xb1a174,
  0x7e8e6c,
  0x9a7c9f,
] as const;

export const DEFAULT_PLAYER_APPEARANCE: CharacterAppearance = Object.freeze({
  schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
  faceShape: "angular",
  bodyShape: "balanced",
  stature: "average",
  skinTone: "warm",
  hairStyle: "parted",
  hairColour: "chestnut",
  eyeColour: "grey",
  outerwear: "wool_coat",
  lowerGarment: "trousers",
  eyewear: "none",
  headwear: "none",
  neckwear: "scarf",
  carriedAccessory: "none",
  colours: Object.freeze({
    outerwear: 0x7f553d,
    lower: 0x343b3b,
    accent: 0xb88959,
  }),
});

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function pick<T>(values: readonly T[], seed: string): T {
  const value = values[stableHash(seed) % values.length];
  if (value === undefined) {
    throw new Error("Appearance option list must not be empty");
  }
  return value;
}

function optionId<T extends readonly Readonly<{ id: string }>[]>(
  values: T,
  seed: string,
): OptionId<T> {
  return pick(values, seed).id as OptionId<T>;
}

function sparseOptionId<
  T extends readonly Readonly<{ id: string } & ({ id: "none" } | object)>[],
>(
  values: T,
  seed: string,
  presencePercent: number,
): OptionId<T> {
  if (stableHash(seed + ":presence") % 100 >= presencePercent) {
    return "none" as OptionId<T>;
  }
  const presentValues = values.filter((option) => option.id !== "none");
  return pick(presentValues, seed + ":style").id as OptionId<T>;
}

export function generateCharacterAppearance(
  stableId: string,
  overrides: Readonly<AppearanceOverrides> = {},
): CharacterAppearance {
  if (stableId.length === 0) {
    throw new Error("Character appearance requires a stable ID");
  }
  const generatedHeadwear = sparseOptionId(
    APPEARANCE_OPTIONS.headwear,
    stableId + ":headwear",
    25,
  );
  const generatedHairStyle = optionId(
    APPEARANCE_OPTIONS.hairStyles,
    stableId + ":hair-style",
  );
  const generated: CharacterAppearance = {
    schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
    faceShape: optionId(APPEARANCE_OPTIONS.faceShapes, stableId + ":face"),
    bodyShape: optionId(APPEARANCE_OPTIONS.bodyShapes, stableId + ":body"),
    stature: optionId(APPEARANCE_OPTIONS.statures, stableId + ":stature"),
    skinTone: optionId(APPEARANCE_OPTIONS.skinTones, stableId + ":skin"),
    hairStyle:
      generatedHeadwear !== "none" && generatedHairStyle === "bun"
        ? "parted"
        : generatedHairStyle,
    hairColour: optionId(
      APPEARANCE_OPTIONS.hairColours,
      stableId + ":hair-colour",
    ),
    eyeColour: optionId(APPEARANCE_OPTIONS.eyeColours, stableId + ":eyes"),
    outerwear: optionId(APPEARANCE_OPTIONS.outerwear, stableId + ":outerwear"),
    lowerGarment: optionId(
      APPEARANCE_OPTIONS.lowerGarments,
      stableId + ":lower-garment",
    ),
    eyewear: sparseOptionId(
      APPEARANCE_OPTIONS.eyewear,
      stableId + ":eyewear",
      24,
    ),
    headwear: generatedHeadwear,
    neckwear: sparseOptionId(
      APPEARANCE_OPTIONS.neckwear,
      stableId + ":neckwear",
      34,
    ),
    carriedAccessory: sparseOptionId(
      APPEARANCE_OPTIONS.carriedAccessories,
      stableId + ":carried",
      28,
    ),
    colours: {
      outerwear: pick(OUTERWEAR_PALETTE, stableId + ":outer-colour"),
      lower: pick(LOWER_PALETTE, stableId + ":lower-colour"),
      accent: pick(ACCENT_PALETTE, stableId + ":accent-colour"),
    },
  };
  return {
    ...generated,
    ...overrides,
    schemaVersion: CHARACTER_APPEARANCE_SCHEMA_VERSION,
    colours: {
      ...generated.colours,
      ...overrides.colours,
    },
  };
}

export function serializePlayerAppearance(
  appearance: Readonly<CharacterAppearance>,
): string {
  validateCharacterAppearance(appearance);
  return JSON.stringify(appearance);
}

export function parsePlayerAppearance(value: string): CharacterAppearance {
  const parsed: unknown = JSON.parse(value);
  validateCharacterAppearance(parsed);
  return structuredClone(parsed);
}

export function validateCharacterAppearance(
  value: unknown,
): asserts value is CharacterAppearance {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Character appearance must be an object");
  }
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== CHARACTER_APPEARANCE_SCHEMA_VERSION) {
    throw new Error("Unsupported character appearance schemaVersion");
  }
  assertOption(record.faceShape, APPEARANCE_OPTIONS.faceShapes, "face shape");
  assertOption(record.bodyShape, APPEARANCE_OPTIONS.bodyShapes, "body shape");
  assertOption(record.stature, APPEARANCE_OPTIONS.statures, "stature");
  assertOption(record.skinTone, APPEARANCE_OPTIONS.skinTones, "skin tone");
  assertOption(record.hairStyle, APPEARANCE_OPTIONS.hairStyles, "hair style");
  assertOption(record.hairColour, APPEARANCE_OPTIONS.hairColours, "hair colour");
  assertOption(record.eyeColour, APPEARANCE_OPTIONS.eyeColours, "eye colour");
  assertOption(record.outerwear, APPEARANCE_OPTIONS.outerwear, "outerwear");
  assertOption(
    record.lowerGarment,
    APPEARANCE_OPTIONS.lowerGarments,
    "lower garment",
  );
  assertOption(record.eyewear, APPEARANCE_OPTIONS.eyewear, "eyewear");
  assertOption(record.headwear, APPEARANCE_OPTIONS.headwear, "headwear");
  assertOption(record.neckwear, APPEARANCE_OPTIONS.neckwear, "neckwear");
  assertOption(
    record.carriedAccessory,
    APPEARANCE_OPTIONS.carriedAccessories,
    "carried accessory",
  );
  if (
    typeof record.colours !== "object" ||
    record.colours === null ||
    Array.isArray(record.colours)
  ) {
    throw new Error("Character appearance colours must be an object");
  }
  const colours = record.colours as Record<string, unknown>;
  for (const key of ["outerwear", "lower", "accent"] as const) {
    const colour = colours[key];
    if (
      typeof colour !== "number" ||
      !Number.isInteger(colour) ||
      colour < 0 ||
      colour > 0xff_ffff
    ) {
      throw new Error("Character appearance has invalid " + key + " colour");
    }
  }
}

function assertOption<T extends readonly Readonly<{ id: string }>[]>(
  value: unknown,
  options: T,
  label: string,
): asserts value is OptionId<T> {
  if (
    typeof value !== "string" ||
    !options.some((option) => option.id === value)
  ) {
    throw new Error("Character appearance has invalid " + label);
  }
}
